import { useMemo } from 'react';
import type { BrandInsightsPageContext } from '@trinity/chat';
import { ChatContextType } from '@trinity/chat';
import { useCreateChatFromAgentContext } from '@trinity/chat-components/hooks/useCreateChatFromAgentContext.js';
import { useAgentChatContextItem } from '../hooks/useAgentChatContextItem';
import { useSegmentationSessionId } from '../hooks/useSegmentationSessionId';
import { ChatContextProviders } from './ChatContextProviders';
import { ChatInterfaceNative } from './ChatInterfaceNative';

const HACKATHON_AGENT_IDENTIFIER = 'hackathon-agent';

/**
 * Container that resolves the agent chat context and renders
 * `ChatInterfaceNative` wrapped in `ChatContextProviders`.
 *
 * Defaults to hackathon-agent. User can switch agents via the
 * context selector dropdown (now enabled).
 */
export function ChatInterfaceNativeContainer({
  onClose,
  sessionId: directSessionId,
  segmentIdentifier,
  segmentationRunId,
  runName,
}: {
  onClose?: () => void;
  sessionId?: string | null;
  segmentIdentifier?: string | null;
  segmentationRunId?: number | null;
  runName?: string | null;
}): React.JSX.Element | null {
  const agentItem = useAgentChatContextItem(HACKATHON_AGENT_IDENTIFIER);
  const runSessionId = useSegmentationSessionId(segmentationRunId ?? null);
  const sessionId = directSessionId || runSessionId;

  const contextData = useMemo<BrandInsightsPageContext.Agent | null>(() => {
    if (!agentItem) return null;
    return {
      type: ChatContextType.Agent,
      data: agentItem,
      metadata: {
        ...(sessionId ? { sessionId } : {}),
        ...(segmentIdentifier ? { segmentIdentifier } : {}),
        ...(runName ? { runName } : {}),
      },
    };
  }, [agentItem, sessionId, segmentIdentifier, runName]);

  if (!contextData) return null;

  return (
    <ChatContextProviders contextData={contextData}>
      <ChatInterfaceNativeInner onClose={onClose} />
    </ChatContextProviders>
  );
}

function ChatInterfaceNativeInner({ onClose }: { onClose?: () => void }): React.JSX.Element {
  const onNewChat = useCreateChatFromAgentContext();
  return <ChatInterfaceNative onClose={onClose} onNewChat={onNewChat} />;
}
